import React from 'react'
import { styled, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import { Text } from '@grupoboticario/flora-react-text'
import { Flex } from '@grupoboticario/flora-react-flex'

import {
  usePagination,
  ButtonTypes,
  UsePaginationProps,
  AccessibilityProps,
} from './libs/usePagination'

const DEFAULT_TAG = Flex

const StyledItem = styled(Text, {
  color: '$linkDefault',
  background: 'transparent',
  border: 'none',
  padding: '0 $2',
  marginInline: '2px',
  cursor: 'pointer',
  userSelect: 'none',
  borderRadius: '$medium',
  height: 40,
  minWidth: 40,
  transition: 'background 0.2s ease-out',
  '&:not(:disabled):not([aria-current="true"]):hover': {
    background: '$actionableHover',
    color: '$actionableContent',
  },
  '&[aria-current="true"]': {
    background: '$actionableActive',
    color: '$actionableContent',
  },
  '& > svg path': {
    fill: 'currentColor',
  },
  '&:disabled': {
    color: '$disabledDark',
    cursor: 'not-allowed',
  },
  variants: {
    variant: {
      alt: {
        '&:disabled': {
          color: '$disabled',
          '& > svg path': {
            fill: '$disabled',
          },
        },
      },
    },
  },
})

const StyledPagination = styled(DEFAULT_TAG, {
  color: '$linkDefault',
  variants: {
    variant: {
      alt: {
        color: '$linkAlt',
        [`${StyledItem}`]: {
          color: '$linkAlt',
        },
        [`${StyledItem}:not(:disabled):not([aria-current="true"]):hover`]: {
          background: '$actionableAltHover',
          color: '$actionableAltContent',
        },
        [`${StyledItem}[aria-current="true"]`]: {
          background: '$actionableAltActive',
          color: '$actionableAltContent',
        },
      },
    },
  },
})

type PaginationButtonProps = AccessibilityProps & {
  label?: string | number
}

const buttons = {
  [ButtonTypes.PAGE_BUTTON]: (props: PaginationButtonProps) => {
    const { label, ...rest } = props

    return (
      <StyledItem as="button" data-flora="pagination-page-button" {...rest}>
        {label}
      </StyledItem>
    )
  },
  [ButtonTypes.DOT_BUTTON]: (props: PaginationButtonProps) => (
    <Flex
      as="span"
      css={{
        minWidth: 20,
        marginInline: '2px',
        justifyContent: 'center',
        alignItems: 'center',
        userSelect: 'none',
      }}
    >
      ...
    </Flex>
  ),
}

export type PaginationProps = Omit<
  FloraVariants<typeof StyledPagination>,
  keyof UsePaginationProps
> &
  UsePaginationProps

/**
 * @component
 * @name Pagination
 * @description Pagination component
 * @tutorial https://flora.gb.tech/docs/components/core/pagination
 */
export const Pagination = forwardRef<'div', PaginationProps>(
  ({ totalRecords, itemsPerPage = 10, page = 0, onChange, ...rest }, ref) => {
    const { buttonList } = usePagination({
      totalRecords,
      itemsPerPage,
      onChange,
      page,
    })

    return (
      <StyledPagination data-flora="pagination" {...rest} ref={ref}>
        {buttonList.map((currentPageButton, index) => {
          const CurrentButton = buttons[currentPageButton.type] as React.FC
          const props = {
            ...currentPageButton.props,
            label: currentPageButton.label,
            ...(currentPageButton.type === ButtonTypes.DOT_BUTTON && { id: currentPageButton.key }),
            variant: rest.variant,
            key: index,
          }

          return <CurrentButton {...props} key={currentPageButton.key} />
        })}
      </StyledPagination>
    )
  },
)
